'use client';

import { Button } from '@curology/ui-components-web/react/Button';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { type QuestionTextModel } from 'survey-core';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { ResubscriptionService } from '@/services/resubscriptionService/client';
import { type ResubscriptionFormulationData } from '@/services/resubscriptionService/types';
import { useFlag } from '@/utils/featureFlags/client';

import {
  getFormulaTargets,
  getProductImages,
  PRODUCT_IDS,
} from './utils/helpers';

const PreviousFormula = ({ question }: { question: QuestionTextModel }) => {
  const { brand } = useFlowContext();
  const [isLoading, setIsLoading] = useState(false);
  const [resubscriptionFormulationData, setResubscriptionFormulationData] =
    useState<ResubscriptionFormulationData>({
      patient: {
        firstname: '',
      },
      formulations: [],
    });

  const { patient, formulations } = resubscriptionFormulationData;
  const isHydroFranchiseActive = useFlag(
    'curology-hydro-plus-cleanser-and-moisturizer',
  );

  useEffect(() => {
    const getResubscriptionFormulationData = async () => {
      const resubscriptionFormulationData =
        await ResubscriptionService.getResubscriptionFormulationData(brand);
      setResubscriptionFormulationData(resubscriptionFormulationData.data.data);
      setIsLoading(false);
    };
    getResubscriptionFormulationData().catch(console.error);
  }, [brand]);

  const handleOnClick = (isKeepingPreviousFormula: string) => {
    question.value = isKeepingPreviousFormula;
    question.surveyModel.nextPage() || question.surveyModel.completeLastPage();
  };

  return (
    <>
      <div
        role="presentation"
        className="cur-bg-surface-neutral cur-text-[#1D1D44]"
      >
        <div className="cur-p-4">
          {formulations
            ?.sort(
              (product1, product2) =>
                product1.productDisplayOrder - product2.productDisplayOrder,
            )
            .map((previousFormula, i) => {
              const ingredientNames = previousFormula.ingredients.map(
                (ingredient) => ingredient.name,
              );
              const formulaTargets = getFormulaTargets(ingredientNames);
              const imageSrc = getProductImages(previousFormula.productId);
              return (
                <div
                  className={i !== formulations.length - 1 ? 'cur-mb-4' : ''}
                  key={i}
                >
                  <div className="cur-flex cur-items-start cur-gap-x-4 cur-pb-4">
                    {imageSrc && (
                      <Image
                        src={imageSrc}
                        alt="Prescription product"
                        width={85}
                        height={108}
                      />
                    )}
                    <div>
                      <div className="cur-text-[20px]	cur-font-bold">
                        {`${patient.firstname}'s ${previousFormula.productName}`}
                        <sup>Rx</sup>
                      </div>
                      <div className="cur-leading-5">
                        {previousFormula.productId ===
                          PRODUCT_IDS.futureProof &&
                          isHydroFranchiseActive && (
                            <div>
                              (Formerly known as Future-Proof<sup>Rx</sup>)
                            </div>
                          )}
                        {previousFormula.ingredients.map((ingredient, i) => (
                          <div key={i}>
                            {ingredient.name} {ingredient.percentage}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="cur-text-[20px] cur-font-bold">
                    Made to treat:
                  </div>
                  <div className="cur-flex cur-flex-wrap cur-gap-y-2 cur-py-4">
                    {formulaTargets.map((target, i) => {
                      return (
                        <div
                          className="cur-flex cur-w-1/2 cur-items-center"
                          key={i}
                        >
                          <target.icon className="cur-pr-2" size="lg" />
                          {target.formattedName}
                        </div>
                      );
                    })}
                  </div>
                  {i !== formulations.length - 1 && (
                    <hr className="cur-border-[1px] cur-text-[#E4E4E4]" />
                  )}
                </div>
              );
            })}
        </div>
      </div>
      <div className="cur-flex cur-flex-col cur-gap-y-4 cur-mt-6">
        <Button
          onClick={() => handleOnClick('yes')}
          className="cur-w-full"
          loading={isLoading}
        >
          Yes, continue with this formula
        </Button>
        <Button
          onClick={() => handleOnClick('no')}
          buttonType="secondary"
          className="cur-w-full"
          loading={isLoading}
        >
          No, I want a new formula
        </Button>
      </div>
    </>
  );
};

export default PreviousFormula;
